import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { Form, FormRow } from '@tanium/react-form';
import { FormTextInput } from '@tanium/react-form-text-input';
import { AsyncStatus, useAsync } from '../../hooks/useAsync';
import { Api } from '../../api';
import { UpdateWorkspaceResponse } from '../../models/TenantModels';

interface UpdateWorkspaceState {
  value?: UpdateWorkspaceResponse;
  status: string;
  execute: (tenantId: string, workspace: string) => Promise<void>;
  reset: () => void;
}

interface ModalProps {
  isOpen: boolean;
  tenantId: string | undefined;
  onRequestClose: () => void;
  updateWorkspace: UpdateWorkspaceState;
}

export const UpdateWorkspaceModal: React.FC<ModalProps> = ({ isOpen, tenantId, onRequestClose, updateWorkspace }) => {
  const { t } = useTranslation();
  const [workspace, setWorkspace] = useState('');
  const [workspaceError, setWorkspaceError] = useState('');
  const [notificationShown, setNotificationShown] = useState(false);
  const { showNotification } = useToastNotificationsControl();

  const formInitialValue = useMemo(() => ({ workspace }), [workspace]);

  const {
    execute: validateWorkspaceExecute,
    error: validateWorkspaceError,
    reset: validateWorkspaceReset,
  } = useAsync(async (workspaceToValidate: string) => {
    return Api.tenants.validateIdentifier(workspaceToValidate).then((resp) => {
      if (!resp.valid) {
        throw new Error(resp.errMsg);
      }
    });
  });

  useEffect(() => {
    if (updateWorkspace.status === AsyncStatus.Success && !notificationShown) {
      const response = updateWorkspace.value;
      setNotificationShown(true);
      showNotification({
        type: 'success',
        contents: `${t('Successfully update workspace to')} ${response?.workspace ?? ''}`,
      });
      setTimeout(() => {
        onRequestClose();
        setWorkspace('');
        updateWorkspace.reset();
      }, 100);
    } else if (updateWorkspace.status === AsyncStatus.Failure && !notificationShown) {
      setNotificationShown(true);
      showNotification({
        type: 'error',
        contents: t('Failed to update workspace'),
      });
      setTimeout(() => {
        onRequestClose();
        setWorkspace('');
        updateWorkspace.reset();
      }, 100);
    }
  }, [updateWorkspace.status]);

  useEffect(() => {
    if (workspace !== '' && workspace.length >= 3) {
      if (!workspace.match(/^[A-Za-z0-9]{3,20}$/)) {
        setWorkspaceError('Workspace must be 3-20 characters long and can only contain letters and numbers');
      } else {
        setWorkspaceError('');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        validateWorkspaceExecute(workspace);
      }
    } else if (workspace !== '' && workspace.length < 3) {
      setWorkspaceError('Workspace must be at least 3 characters long');
    } else {
      setWorkspaceError('');
      validateWorkspaceReset();
    }
  }, [workspace]);

  useEffect(() => {
    if (validateWorkspaceError) {
      setWorkspaceError(validateWorkspaceError.message || 'Workspace is not available');
    }
  }, [validateWorkspaceError]);

  useEffect(() => {
    if (!isOpen) {
      validateWorkspaceReset();
      setWorkspaceError('');
      setWorkspace('');
      setNotificationShown(false);
    }
  }, [isOpen, validateWorkspaceReset]);

  const handleCancel = () => {
    onRequestClose();
    setWorkspace('');
    setWorkspaceError('');
    setNotificationShown(false);
    updateWorkspace.reset();
    validateWorkspaceReset();
  };

  const handleConfirm = async () => {
    if (tenantId && workspace) {
      setNotificationShown(false);
      await updateWorkspace.execute(tenantId, workspace);
    }
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={t('Update Workspace')}
      onConfirm={handleConfirm}
      confirmLabel={t('Update')}
      isConfirmDisabled={updateWorkspace.status === AsyncStatus.Pending || !workspace || !!workspaceError}
      onCancel={handleCancel}
    >
      <>{t('Specify the new workspace identifier for your Tanium Account.')}</>
      <br />
      <br />
      <Form
        initialValue={formInitialValue}
        buttons={<></>}
        onValueChange={(e) => {
          setWorkspace((e.value.workspace as string).toLocaleLowerCase());
        }}
      >
        <FormRow dataKey={'workspace'} label={t('Workspace')} required errors={workspaceError}>
          <FormTextInput />
        </FormRow>
      </Form>
    </ConfirmModal>
  );
};
